'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { TDynamicData } from '../../types/dynamic-data';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Paragraph } from '../ui/paragraph';
import { Heading } from '../ui/heading';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { LuMinus, LuPlus } from 'react-icons/lu';

export interface ContactCardProps {
  description: string;
  icon: string;
  value: string;
  href: string;
}

export function FAQ({
  generalLayout,
  title,
  description,
  faqs,
}: {
  generalLayout: GeneralLayout['data'];
  title: string;
  description: string;
  faqs: TDynamicData[];
}) {
  const contacts = [
    {
      icon: '/images/icons/footer/call-calling-black.svg',
      description: '<p>Potrebujete poradiť?<br/>Zavolajte nám.</p>',
      value: generalLayout.contact?.phone || '',
      href: `tel:${generalLayout.contact?.phone || ''}`,
    },
    {
      icon: '/images/icons/footer/sms-tracking-black.svg',
      description: '<p>Alebo nám napíšte e-mail a my vám čo najskôr odpovieme.</p>',
      value: generalLayout.contact?.email || '',
      href: `mailto:${generalLayout.contact?.email || ''}`,
    },
  ] as ContactCardProps[];

  return (
    <section className={cn('custom-section', 'ea-section__xxl-t ea-section__xl-b', 'bg-[#FAFAFA]')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:gap-8 md:flex-row md:justify-between lg:gap-12',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-12 md:max-w-[539px]">
          <div className="flex w-full flex-col items-start justify-start gap-6">
            <Heading tag="h2" title={title} />
            <Paragraph size="md" color="muted-foreground" innerHtml={description} tag="p" />
          </div>
          <div className="hidden w-full grid-cols-2 gap-[1px] bg-border sm:grid">
            {contacts.map(contact => (
              <ContactCard key={contact.value} {...contact} />
            ))}
          </div>
        </div>
        <Accordion
          type="multiple"
          className={cn(
            'flex w-full flex-col items-start justify-start gap-2 md:max-w-[604px]',
            'ea-faq__accordion',
          )}
        >
          {faqs.map(item => (
            <AccordionItem
              value={item.name}
              key={item?.name}
              className={cn(
                'w-full rounded-[8px] bg-white p-4 sm:p-5 lg:p-6',
                'ea-faq__accordion-item',
              )}
            >
              <AccordionTrigger
                hideIcon={true}
                className={cn('w-full gap-4 p-0 text-left', 'ea-faq__accordion-trigger')}
              >
                <Heading tag="h6" semanticTag="span" title={item.name} />
                <div
                  className={cn(
                    'minus',
                    'flex h-6 min-w-6 max-w-6 items-center justify-center rounded-[4px] bg-primary',
                  )}
                >
                  <LuMinus className="h-4 w-4 text-white" />
                </div>
                <div
                  className={cn(
                    'plus',
                    'flex h-6 min-w-6 max-w-6 items-center justify-center rounded-[4px] bg-primary',
                  )}
                >
                  <LuPlus className="h-4 w-4 text-white" />
                </div>
              </AccordionTrigger>
              <AccordionContent className={cn('w-full p-0 text-left', 'ea-faq__accordion-wrapper')}>
                <Paragraph
                  innerHtml={item.fields[0]?.value}
                  color="muted-foreground"
                  size="md"
                  className={cn('w-full pt-4 sm:pt-5 lg:pt-6', 'ea-faq__accordion-content')}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function ContactCard({
  icon,
  description,
  value,
  href,
  className,
}: ContactCardProps & { className?: string }) {
  return (
    <Link
      prefetch={false}
      target="_blank"
      href={href}
      className={cn(
        'flex flex-row items-start justify-start gap-4 bg-[#FAFAFA] p-0 transition-all hover:opacity-90 sm:flex-col sm:p-4 xl:p-6',
        className,
      )}
    >
      <Image
        src={icon}
        alt={value}
        width={24}
        height={24}
        className="max-h-6 min-h-6 min-w-6 max-w-6"
      />
      <div className="flex w-full flex-col items-start justify-start gap-2.5 sm:gap-4">
        <Paragraph innerHtml={description} color="muted-foreground" size="sm" />
        <strong className="text-xs font-bold text-black sm:text-sm">{value}</strong>
      </div>
    </Link>
  );
}
